import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TablePagination,
} from "@mui/material";
import { Column, Pagination } from "src/api/types";
import { ROWS_PER_PAGE } from "src/constants/rowPerPage";
import TableHeaderCell from "src/shared/TableHeaderCell";
import DataTableRow from "../DataTableRow";
import { theme } from "src/assets/theme";

type Props<T> =
  | {
      primaryKey: keyof T;
      data: T[];
      columns: Column<T>[];
      height?: number | string;
      pagination?: never;
      total?: never;
      colorful?: boolean;
    }
  | {
      primaryKey: keyof T;
      data: T[];
      columns: Column<T>[];
      height?: number | string;
      pagination: Pagination;
      total: number;
      colorful?: boolean;
    };

function DataTable<T>(props: Props<T>) {
  const {
    data,
    columns,
    height = "100%",
    primaryKey,
    pagination,
    total,
    colorful = false,
  } = props;

  return (
    <Box>
      <TableContainer
        sx={{
          height,
          width: "100%",
          overflowX: "auto",
        }}
      >
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableHeaderCell
                  key={column.id}
                  column={column}
                  color={colorful ? theme.palette.teal[500] : null}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 ? (
              data.map((row) => (
                <DataTableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={row[primaryKey] as unknown as string}
                  sx={{
                    backgroundColor: colorful ? theme.palette.teal[200] : null,
                  }}
                >
                  {columns.map((column) => (
                    <TableCell
                      width={column.minWidth}
                      key={column.id}
                      align={column.align}
                    >
                      {column.render?.(row)}
                    </TableCell>
                  ))}
                </DataTableRow>
              ))
            ) : (
              <TableRow>
                <TableCell align="center" colSpan={columns.length}>
                  <Typography>No data available</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination != null ? (
        <TablePagination
          sx={{ height: 52, mr: 10 }}
          rowsPerPageOptions={ROWS_PER_PAGE}
          component="div"
          count={total}
          rowsPerPage={pagination.limit}
          page={pagination.page}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
        />
      ) : null}
    </Box>
  );
}

export default DataTable;
